import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import {
  TextField,
  Radio,
  RadioGroupField,
  View,
} from "@aws-amplify/ui-react";
import "@aws-amplify/ui-react/styles.css";
import { Box, Card } from "theme-ui";
import {
  Heading,
  Button as LumosButton,
  useNotify,
} from "@loanpal/lumos__core";
import { mintPaymentLink } from "../../utils/api";
import logo from "../../images/goodleap-white-orange.svg";

/** Same Passion / Grape tokens as the pay flow. */
const PAY = {
  radiusSm: "10px",
  accent: "#472BA4",
  accentHover: "#200F51",
  accentSoft: "rgba(71, 43, 164, 0.1)",
  pageBg: "#F6F5F4",
};

const PROS_CREATE_BASE = "https://origin.goodleap.com/payments/create";

const DEST_STANDALONE = "standalone";
const DEST_PROS = "pros";

const primaryButtonSx = {
  borderRadius: PAY.radiusSm,
  height: 40,
  px: 3,
  fontWeight: 600,
  bg: PAY.accent,
  color: "white",
  border: "1px solid",
  borderColor: PAY.accent,
  "&:hover": {
    bg: PAY.accentHover,
    borderColor: PAY.accentHover,
    color: "white",
  },
  "&:disabled": {
    opacity: 0.5,
    cursor: "default",
  },
};

const secondaryButtonSx = {
  borderRadius: PAY.radiusSm,
  height: 40,
  px: 3,
  fontWeight: 600,
  color: PAY.accent,
  borderColor: PAY.accent,
  bg: "transparent",
  "&:hover": {
    bg: PAY.accentSoft,
    color: PAY.accentHover,
    borderColor: PAY.accentHover,
  },
};

/**
 * Public helper page to generate a signed Pay Link (SharedApp id + Reference ID).
 * Same as the public POST /payment-link API — no partner API keys or Cognito.
 *
 * /pay/generate/:id (www) and /generate/:id (pay.goodleap.*) pre-fill/lock the pay link id.
 */
const REF_ALLOWED_PATTERN = /^[A-Za-z0-9_-]+$/;
const REF_HELP =
  "Letters (A–Z, a–z), digits (0–9), hyphen (-), and underscore (_) only. No spaces or other special characters.";

function buildProsPaymentUrl(reference) {
  return `${PROS_CREATE_BASE}?ref=${encodeURIComponent(reference)}`;
}

const GeneratePaymentLink = () => {
  const { id: idFromPath } = useParams();
  const lockedPayId = String(idFromPath || "").trim();
  const { addSnack, addToast } = useNotify();
  const [payLinkId, setPayLinkId] = useState(lockedPayId);
  const [ref, setRef] = useState("");
  const [ttlDays, setTtlDays] = useState("90");
  const [destination, setDestination] = useState(DEST_STANDALONE);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const isPros = destination === DEST_PROS;

  useEffect(() => {
    if (lockedPayId) setPayLinkId(lockedPayId);
  }, [lockedPayId]);

  const handleGenerate = async () => {
    const sid = (lockedPayId || payLinkId).trim();
    const reference = ref.trim();
    if (!reference) {
      addSnack({
        variant: "warning",
        message: "Reference ID is required.",
      });
      return;
    }
    if (!isPros && !sid) {
      addSnack({
        variant: "warning",
        message: "PayLink ID and Reference ID are required.",
      });
      return;
    }
    if (reference.length > 128) {
      addSnack({
        variant: "warning",
        message: "Reference ID must be 128 characters or fewer.",
      });
      return;
    }
    if (!REF_ALLOWED_PATTERN.test(reference)) {
      addSnack({
        variant: "warning",
        message: `Invalid Reference ID. ${REF_HELP}`,
      });
      return;
    }

    setBusy(true);
    setResult(null);
    try {
      if (isPros) {
        setResult({
          url: buildProsPaymentUrl(reference),
          ref: reference,
          destination: DEST_PROS,
          expiresAt: null,
        });
        addToast({ variant: "success", message: "GoodLeap Pros link created." });
        return;
      }

      const days = Number(ttlDays);
      const ttlSeconds =
        Number.isFinite(days) && days > 0
          ? Math.floor(days * 24 * 60 * 60)
          : undefined;
      const data = await mintPaymentLink({
        payLinkId: sid,
        ref: reference,
        ttlSeconds,
      });
      setResult({ ...data, destination: DEST_STANDALONE });
      addToast({ variant: "success", message: "Payment link created." });
    } catch (err) {
      console.log("mint payment link failed", err);
      addSnack({
        variant: "danger",
        message: err?.message || "Failed to create payment link",
      });
    } finally {
      setBusy(false);
    }
  };

  const copyUrl = async () => {
    const url = result?.url;
    if (!url) return;
    try {
      await navigator.clipboard.writeText(url);
      addToast({ variant: "success", message: "Copied to clipboard." });
    } catch {
      addSnack({ variant: "danger", message: "Could not copy link." });
    }
  };

  const openUrl = () => {
    const url = result?.url;
    if (!url) return;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  return (
    <Box sx={{ maxWidth: "100%", mx: "auto", bg: PAY.pageBg, minHeight: "100vh" }}>
      <Box>
        <Heading
          className="heading-box"
          sx={{
            width: 960,
            maxWidth: "100%",
            margin: "0 auto",
            background:
              "linear-gradient(93.207deg, #200F51 0%, #200F51 40%, #472BA4 160%)",
            borderBottom: "4px solid #FF8300",
            borderBottomLeftRadius: "4px",
            borderBottomRightRadius: "4px",
            overflow: "hidden",
          }}
        >
          <img
            src={logo}
            alt="GoodLeap"
            style={{
              width: 150,
              margin: "0 auto",
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              padding: 4,
            }}
            className="logo"
          />
        </Heading>
      </Box>

      <Box sx={{ maxWidth: 720, mx: "auto", px: 3, py: 4 }}>
        <Heading variant="h4" sx={{ mb: 2, color: PAY.accentHover }}>
          Generate Payment Link
        </Heading>
        <Box sx={{ mb: 3, color: "neutral.d2", fontSize: 2 }}>
          Pass your PayLink ID and reference ID.
        </Box>

        <Card variant="float" sx={{ p: 4 }}>
          <View>
            <TextField
              label="PayLink ID"
              descriptiveText={
                isPros
                  ? "Not used for GoodLeap Pros links"
                  : "Get it from http://pay.goodleap.link/{payLinkId}"
              }
              value={lockedPayId || payLinkId}
              onChange={(e) => {
                if (lockedPayId) return;
                setPayLinkId(e.target.value);
              }}
              isReadOnly={Boolean(lockedPayId)}
              isDisabled={Boolean(lockedPayId) || isPros}
              marginBottom="1rem"
            />
            <Box sx={{ mb: 3 }}>
              <TextField
                label="Reference ID"
                descriptiveText={REF_HELP}
                value={ref}
                onChange={(e) => setRef(e.target.value)}
                hasError={
                  Boolean(ref.trim()) &&
                  !REF_ALLOWED_PATTERN.test(ref.trim())
                }
                marginBottom="0"
              />
              {Boolean(ref.trim()) &&
              !REF_ALLOWED_PATTERN.test(ref.trim()) ? (
                <Box
                  sx={{
                    mt: "2px",
                    mb: 0,
                    color: "var(--amplify-colors-font-error, #ba181b)",
                    fontSize: 1,
                    lineHeight: 1.35,
                  }}
                >
                  {REF_HELP}
                </Box>
              ) : null}
            </Box>
            <TextField
              label="Link TTL (days)"
              type="number"
              value={ttlDays}
              onChange={(e) => setTtlDays(e.target.value)}
              isDisabled={isPros}
              descriptiveText={
                isPros ? "Not used for GoodLeap Pros links" : undefined
              }
              marginBottom="1rem"
            />
            <Box sx={{ mb: 3 }}>
              <RadioGroupField
                legend="Open in"
                name="paymentLinkDestination"
                value={destination}
                onChange={(e) => {
                  setDestination(e.target.value);
                  setResult(null);
                }}
                marginBottom="0"
              >
                <Radio value={DEST_STANDALONE}>
                  Standalone PayLink page (pay.goodleap.link)
                </Radio>
                <Radio value={DEST_PROS}>GoodLeap Pros app</Radio>
              </RadioGroupField>
              <Box
                sx={{
                  mt: 1,
                  fontSize: 1,
                  fontStyle: "italic",
                  color: "neutral.d2",
                  lineHeight: 1.4,
                }}
              >
                Note: You need to have GoodLeap Pros app installed and need an
                authenticated user in GoodLeap to be able to process a payment.
              </Box>
            </Box>
            <LumosButton
              type="button"
              onClick={handleGenerate}
              disabled={busy}
              sx={primaryButtonSx}
            >
              {busy
                ? "Generating…"
                : isPros
                  ? "Generate Pros link"
                  : "Generate signed link"}
            </LumosButton>
          </View>

          {result?.url ? (
            <Box sx={{ mt: 4 }}>
              <Box sx={{ fontWeight: 600, mb: 2 }}>
                {result.destination === DEST_PROS
                  ? "GoodLeap Pros URL"
                  : "Signed URL"}
              </Box>
              <Box
                as="input"
                readOnly
                value={result.url}
                onClick={(e) => e.target.select?.()}
                sx={{
                  width: "100%",
                  fontSize: 1,
                  p: 2,
                  mb: 2,
                  borderRadius: "8px",
                  border: "1px solid",
                  borderColor: "neutral.l3",
                  bg: "neutral.l4",
                }}
              />
              <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                <LumosButton
                  type="button"
                  variant="secondary"
                  onClick={copyUrl}
                  sx={secondaryButtonSx}
                >
                  Copy link
                </LumosButton>
                <LumosButton
                  type="button"
                  variant="secondary"
                  onClick={openUrl}
                  sx={secondaryButtonSx}
                >
                  Open link
                </LumosButton>
              </Box>
              <Box sx={{ mt: 3, fontSize: 1, color: "neutral.d2" }}>
                {result.destination === DEST_PROS ? (
                  <>
                    Destination: GoodLeap Pros
                    <br />
                    Ref: {result.ref}
                  </>
                ) : (
                  <>
                    Expires: {result.expiresAt || "—"}
                    <br />
                    Ref: {result.ref}
                  </>
                )}
              </Box>
            </Box>
          ) : null}
        </Card>
      </Box>
    </Box>
  );
};

export default GeneratePaymentLink;
